import type { Locale } from "./i18n";

import { setLanguage, t } from "./i18n";
import recordingBarCss from "./recording-bar.css?inline";
import { attachStyles } from "./styles";

const HOST_ID = "weloop-recording-bar";

export interface RecordingBarHandle {
	readonly dispose: () => void;
}

const formatElapsed = (ms: number): string => {
	const totalSeconds = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Floating top-of-viewport bar shown on the host page while a user-triggered
 * session recording is in progress. The widget iframe is closed at that
 * point so the user can interact with the host page directly; the bar gives
 * them an always-visible Stop control plus an elapsed-time indicator.
 *
 * `startedAt` is the recording's own start time, not this page's – a recording
 * that spans several host pages re-mounts the bar on every load and the timer
 * has to keep counting from where the user started.
 *
 * The bar lives in a closed shadow root for style isolation, matching the
 * FAB pattern. Z-index is the maximum 32-bit signed value, the same one the
 * iframe uses – the bar lives in the same stacking context as the rest of
 * the snippet's host-page surface.
 */
export const mountRecordingBar = (
	locale: Locale,
	startedAt: number,
	onStop: () => void,
): RecordingBarHandle => {
	const host = document.createElement("div");
	host.id = HOST_ID;
	setLanguage(host, locale);
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, recordingBarCss);

	const bar = document.createElement("div");
	bar.className = "bar";

	const dot = document.createElement("span");
	dot.className = "dot";
	dot.setAttribute("aria-hidden", "true");

	// The live region is the label alone, filled on the first tick: a region
	// must already be on the page for a change to it to be announced, so
	// "Recording" is read once. The ticking timer stays outside – inside it,
	// the elapsed time would be read every second.
	const label = document.createElement("span");
	label.className = "label";
	label.setAttribute("role", "status");

	const timer = document.createElement("span");
	timer.className = "timer";
	timer.setAttribute("role", "timer");
	timer.textContent = formatElapsed(Date.now() - startedAt);

	const stop = document.createElement("button");
	stop.className = "stop";
	stop.type = "button";
	stop.textContent = t(locale, "stop_recording");
	stop.addEventListener("click", onStop);

	bar.append(dot, label, timer, stop);
	shadow.append(bar);
	document.body.append(host);

	// The panel held focus inside the widget frame, which is now hidden – like
	// the screenshot bar, pull focus onto the one action left on screen.
	// Firefox can keep focus in the frame until it is hidden, a moment later,
	// so focus is taken again then unless the user already moved it.
	const focusStop = (): void => {
		stop.focus({ preventScroll: true });
	};
	focusStop();
	const refocus = setTimeout(() => {
		const active = document.activeElement;
		if (active === document.body || active?.tagName === "IFRAME") {
			focusStop();
		}
	}, 300);

	const interval = setInterval(() => {
		if (label.textContent === "") {
			label.textContent = t(locale, "recording");
		}
		timer.textContent = formatElapsed(Date.now() - startedAt);
	}, 250);

	return {
		dispose: () => {
			clearInterval(interval);
			clearTimeout(refocus);
			stop.removeEventListener("click", onStop);
			host.remove();
		},
	};
};
